import { useEffect, useMemo, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { NODE_CATEGORIES } from './nodeCategories'
import { getSmartSuggestions } from './ivrSmartSuggestions'
import { usePlanRights } from '@/hooks/usePlanRights'

interface Props {
  parentNodeType: string
  handleId: string
  position: 'right' | 'bottom'
  onAddNode: (nodeType: string) => void
}

/**
 * Popover "+" qui apparaît sur les handles non connectés.
 * Affiche les suggestions intelligentes en haut + toutes les catégories.
 */
export function IvrAddNodePopover({
  parentNodeType,
  handleId,
  position,
  onAddNode,
}: Props) {
  const { t } = useTranslation()
  // Le nœud « Vers équipe » est inclus à partir de Team.
  const { allowed } = usePlanRights()
  const verrouille = (type: string) => (type === 'route_team' || type === 'queue') && !allowed('numeros_partages')
  const [isOpen, setIsOpen] = useState(false)
  const popoverRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen) return
    const handler = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as HTMLElement)
      ) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [isOpen])

  // Suggestions intelligentes selon parent + handle
  const suggestions = useMemo(
    () => getSmartSuggestions(parentNodeType, handleId),
    [parentNodeType, handleId],
  )

  // Map flat type → définition
  const nodeMap = useMemo(() => {
    const map = new Map<
      string,
      { label: string; icon: typeof Plus; categoryLabelKey: string }
    >()
    for (const cat of NODE_CATEGORIES) {
      for (const n of cat.nodes.filter((d) => !d.horsPalette)) {
        map.set(n.type, {
          label: t(n.labelKey),
          icon: n.icon,
          categoryLabelKey: cat.labelKey,
        })
      }
    }
    return map
  }, [t])

  const suggestedNodes = suggestions
    .map((s) => {
      const info = nodeMap.get(s.type)
      if (!info) return null
      return { type: s.type, ...info }
    })
    .filter(
      (n): n is { type: string; label: string; icon: typeof Plus; categoryLabelKey: string } =>
        n !== null,
    )

  const handleSelect = (nodeType: string) => {
    setIsOpen(false)
    onAddNode(nodeType)
  }

  return (
    <div
      ref={popoverRef}
      className={cn(
        'absolute z-50',
        position === 'right'
          ? 'left-full top-1/2 -translate-y-1/2 ml-2'
          : 'top-full left-1/2 -translate-x-1/2 mt-2',
      )}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          setIsOpen((v) => !v)
        }}
        className={cn(
          'w-5 h-5 rounded-full flex items-center justify-center transition-all duration-200 shadow-sm',
          'bg-blue-500 hover:bg-blue-600 text-white hover:scale-110',
        )}
        title={t('manager.flows.editor.addNode.title')}
      >
        <Plus className="w-3 h-3" />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{
              opacity: 0,
              scale: 0.9,
              y: position === 'bottom' ? -4 : 0,
              x: position === 'right' ? -4 : 0,
            }}
            animate={{ opacity: 1, scale: 1, y: 0, x: 0 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.15 }}
            className={cn(
              'absolute w-52 bg-[#1e2035] border border-white/10 rounded-lg shadow-xl overflow-hidden',
              position === 'right'
                ? 'left-full top-1/2 -translate-y-1/2 ml-2'
                : 'top-full left-1/2 -translate-x-1/2 mt-2',
            )}
            onClick={(e) => e.stopPropagation()}
            onWheel={(e) => e.stopPropagation()}
          >
            {suggestedNodes.length > 0 && (
              <div className="p-2 border-b border-white/[0.06]">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-white/25 px-1.5 mb-1.5">
                  {t('manager.flows.editor.addNode.suggestions')}
                </p>
                <div className="space-y-0.5">
                  {suggestedNodes.map((node) => {
                    const Icon = node.icon
                    return (
                      <button
                        key={node.type}
                        type="button"
                        onClick={() => handleSelect(node.type)}
                        disabled={verrouille(node.type)}
                        title={verrouille(node.type) ? t('plan.includedIn', { plan: 'Team' }) : undefined}
                        className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-[12px] text-white/70 hover:bg-white/[0.08] hover:text-white/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Icon className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                        <span className="truncate">{node.label}</span>
                      </button>
                    )
                  })}
                </div>
              </div>
            )}

            <div className="p-2 max-h-52 overflow-y-auto">
              {NODE_CATEGORIES.map((cat) => (
                <div key={cat.id} className="mb-2 last:mb-0">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-white/25 px-1.5 mb-1">
                    {t(cat.labelKey)}
                  </p>
                  <div className="space-y-0.5">
                    {cat.nodes.filter((d) => !d.horsPalette).map((node) => {
                      const Icon = node.icon
                      return (
                        <button
                          key={node.type}
                          type="button"
                          onClick={() => handleSelect(node.type)}
                          disabled={verrouille(node.type)}
                          title={verrouille(node.type) ? t('plan.includedIn', { plan: 'Team' }) : undefined}
                          className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-[12px] text-white/70 hover:bg-white/[0.08] hover:text-white/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <Icon className="w-3.5 h-3.5 shrink-0 opacity-60" />
                          <span className="truncate">{t(node.labelKey)}</span>
                        </button>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
